import {
  apiUrls,
  StableCoinTokens,
  Tokens,
  tokensConfig,
  TOKEN_A_AMOUNT_MIN,
  TOKEN_B_AMOUNT_MIN,
  SLIPPAGE_TOLERANCE,
  FRACTIONAL_DIGITS,
  ZERO_VALUE,
  ANIMATION_DURATION,
  MULTIPLIER,
  TOKEN_UNIT,
  RATE_UPDATE_INTERVAL
} from '~/utils/constants';
import tokens from 'public/img/tokens';
import { useDebounceFn, useIntervalFn } from '@vueuse/core';
import type {
  ExchangePurchasedData,
  ExchangeToken,
  ExchangeConfirmData,
  DexTokenPairsResponse,
  TokenPair,
  DexTokenTransformed
} from '~/types/dex';
import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { formatEther } from 'ethers';
import { BigNumber } from 'bignumber.js';
import { getCancelErrorMessage, modifyHistoryHashStatus, saveHashToLocalStorage } from '~/utils';
import { isToken } from '~/types/typeguards';
import { findTokenByPartialKey, splitCamelCase } from '~/types/dexHelper';
import { extractReceivedAmountFromLogs } from '~/utils/extractReceivedAmountFromLogs';

export default async (isModalVisible: Ref<boolean>) => {
  const { walletInfo } = useWalletInfo();
  const store = useMainStore();
  const { t } = useI18n();
  const { blockchain, apiUrl } = useEnvs();
  const { address } = useWeb3ModalAccount();
  const { $errorHandler } = useNuxtApp();
  const { approveDex, swapExactTokensForTokens } = useStakingMethods();
  const { getAmountsOut } = useTokensReader();

  const isExchangeLoading = ref(false);
  const isSellTokenA = ref(true);
  const isDexDataLoading = ref(false);
  const isAnimating = ref(false);
  const isRateTokenALoading = ref(false);
  const isRateTokenBLoading = ref(false);
  const isActualRateLoading = ref(false);
  const isProgrammaticUpdate = ref(false);
  const isSwapped = ref(false);
  const dexData = ref<DexTokenPairsResponse | null>(null);
  const showConfirmModal = ref(false);
  const showSuccessModal = ref(false);
  const availableTokensA = ref<DexTokenTransformed[]>([]);
  const availableTokensB = ref<DexTokenTransformed[]>([]);
  const actualRate = ref('');
  const tokenA_amount = ref(TOKEN_A_AMOUNT_MIN);
  const tokenB_amount = ref(TOKEN_B_AMOUNT_MIN);
  const exchangeDataPurchased = ref<ExchangePurchasedData | null>(null);
  const balanceKeyA = ref(0);
  const balanceKeyB = ref(0);

  const getTokenPriority = (tokenKey: string): number => {
    if (tokenKey === Tokens.pearl) return 0;
    if (tokenKey === Tokens.blackPearl) return 1;
    return 2;
  };

  const sortByTokenPriority = <T>(items: T[], extractKey: (item: T) => string): T[] =>
    items.slice().sort((a, b) => getTokenPriority(extractKey(a)) - getTokenPriority(extractKey(b)));

  const tokenA = reactive<ExchangeToken>({
    key: '',
    label: '',
    icon: '',
    amount: TOKEN_A_AMOUNT_MIN.toString(),
    isSkipApprove: false
  });

  const tokenB = reactive<ExchangeToken>({
    key: '',
    label: '',
    icon: '',
    amount: TOKEN_B_AMOUNT_MIN.toString(),
    isSkipApprove: false
  });

  const tokenSpend = computed(() => (isSellTokenA.value ? tokenA : tokenB));
  const tokenReceive = computed(() => (isSellTokenA.value ? tokenB : tokenA));

  const balanceTokenAKey = computed(() => `${tokenATokenAddress.value}-${balanceKeyA.value}`);
  const balanceTokenBKey = computed(() => `${tokenBTokenAddress.value}-${balanceKeyB.value}`);

  const exchangeData = computed(() => {
    return {
      tokenSpendAmount: formatInputWithSpaces(BigNumber(tokenSpend.value.amount).toString()),
      tokenSpendName: tokenSpend.value.label,
      tokenSpendIcon: tokenSpend.value.icon,
      tokenGetAmount: formatInputWithSpaces(BigNumber(tokenReceive.value.amount).toString()),
      tokenGetName: tokenReceive.value.label,
      tokenGetIcon: tokenReceive.value.icon,
      rate: actualRateFormatted.value,
      slippageTolerance: `${SLIPPAGE_TOLERANCE}%`
    } as ExchangeConfirmData;
  });

  const tokenATokenAddress = computed(() => blockchain.contracts[tokenA.key as StableCoinTokens]);
  const tokenBTokenAddress = computed(() => blockchain.contracts[tokenB.key as Tokens]?.addresses.contract as string);

  const tokenSpendAddress = computed(() => (isSellTokenA.value ? tokenATokenAddress.value : tokenBTokenAddress.value));
  const tokenReceiveAddress = computed(() =>
    isSellTokenA.value ? tokenBTokenAddress.value : tokenATokenAddress.value
  );

  const isEnoughBalance = computed(() => {
    const amount = BigNumber(tokenSpend.value.amount ?? 0);
    if (!amount.isFinite() || amount.lte(0)) {
      return false;
    }

    const balance = BigNumber(tokenSpendBalance.value ?? 0);
    return balance.gte(amount);
  });

  const usdtContract = ref(blockchain.contracts.usdt);
  const { balance: balanceA, pullBalance: pullBalanceA } = await useBalance(usdtContract);
  const { balance: balanceB, pullBalance: pullBalanceB } = await useBalance(tokenBTokenAddress);

  const tokenSpendBalance = computed(() => (isSellTokenA.value ? balanceA.value : balanceB.value));

  const tokenA_balance = computed(() => Number(balanceA.value));
  const tokenB_balance = computed(() => Number(balanceB.value));

  const actualRateFormatted = computed(
    () =>
      `${TOKEN_UNIT} ${tokenSpend.value.label.toUpperCase()} = ${actualRate.value} ${tokenReceive.value.label.toUpperCase()}`
  );

  const availableTokenAOptions = computed(() => {
    const options = availableTokensA.value.map((token) => {
      return {
        key: token.key,
        label: tokensConfig[token.key as StableCoinTokens]?.methodName ?? splitCamelCase(token.name),
        icon: tokens[token.key as StableCoinTokens] as string
      };
    });
    return sortByTokenPriority(options, (item) => item.key as string);
  });

  const availableTokenBOptions = computed(() =>
    sortByTokenPriority(
      availableTokensB.value.map((token) => {
        const key = isToken(token.name) ? token.name : (findTokenByPartialKey(token.name) as Tokens);
        const label = tokensConfig[key as Tokens]?.methodName ?? splitCamelCase(token.name);

        return {
          key,
          label,
          icon: tokens[key as Tokens] as string,
          address: token.address
        };
      }),
      (item) => item.key as string
    )
  );

  const availableTokenAOptionsExcludingSelected = computed(() =>
    availableTokenAOptions.value.filter((item) => item.key !== tokenA.key)
  );

  const availableTokenBOptionsExcludingSelected = computed(() =>
    availableTokenBOptions.value
      .filter((item) => item.key !== tokenB.key)
      .map((token) => ({
        ...token,
        balance: getTokenBalance(token.address)
      }))
  );

  const tokenBAddresses = computed(() => availableTokenBOptions.value.map((token) => token.address));
  const { getTokenBalance } = await useTokenBalances(tokenBAddresses);

  const swapTokens = useDebounceFn(async () => {
    isAnimating.value = true;
    isSellTokenA.value = !isSellTokenA.value;
    isSwapped.value = !isSwapped.value;

    setTimeout(() => {
      isAnimating.value = false;
    }, ANIMATION_DURATION);
  });

  const changeToken = async (newToken: Tokens) => {
    tokenB.key = newToken;
    tokenB.label = tokensConfig[newToken]?.methodName;
    tokenB.icon = tokens[newToken] as string;
  };

  const getActualRate = async (): Promise<string> => {
    try {
      const spendAddress = tokenSpendAddress.value;
      const receiveAddress = tokenReceiveAddress.value;

      if (!spendAddress || !receiveAddress) {
        console.warn('getActualRate: Skipping rate calculation because token addresses are not yet defined.');
        return actualRate.value;
      }
      const amounts = await getAmountsOut(TOKEN_UNIT, tokenSpendAddress.value, tokenReceiveAddress.value);

      const amountOutFormatted = BigNumber(formatEther(amounts[1]));
      const result = amountOutFormatted.toFormat(FRACTIONAL_DIGITS, BigNumber.ROUND_DOWN, {
        groupSeparator: '',
        decimalSeparator: '.'
      });

      return result;
    } catch (err) {
      console.log(err);
      return actualRate.value;
    } finally {
      isActualRateLoading.value = false;
    }
  };

  const exchange = async () => {
    try {
      if (!isEnoughBalance.value || !tokenSpendBalance.value || !address.value) {
        return;
      }

      showConfirmModal.value = false;
      isExchangeLoading.value = true;

      const amountOut = BigNumber(tokenReceive.value.amount).multipliedBy(MULTIPLIER).toFixed(FRACTIONAL_DIGITS);

      const deadline = Math.floor(Date.now() / 1000) + 1200; // 20 min

      if (!tokenSpend.value.isSkipApprove) {
        const transactionApprove = await approveDex(tokenSpend.value.amount, tokenSpendAddress.value);

        if (!transactionApprove?.hash) {
          return;
        }

        saveHashToLocalStorage(t('notificationApprove'), transactionApprove.hash);
        store.updateVersion();

        await transactionApprove?.wait();
      }

      exchangeDataPurchased.value = {
        tokenIcon: exchangeData.value.tokenGetIcon,
        tokenName: exchangeData.value.tokenGetName,
        amount: null
      };

      const transactionExchange = await swapExactTokensForTokens(
        tokenSpend.value.amount.toString(),
        amountOut,
        tokenSpendAddress.value,
        tokenReceiveAddress.value,
        address.value,
        deadline
      );

      const res = await transactionExchange?.wait();

      if (!res?.logs) {
        return;
      }

      const receivedAmount = extractReceivedAmountFromLogs(res.logs, blockchain.swapEvent);
      const receivedAmountFormatted = formatInputWithSpaces(receivedAmount);

      exchangeDataPurchased.value.amount = receivedAmountFormatted;
      showSuccessModal.value = true;

      if (!transactionExchange?.hash) {
        return;
      }
      saveHashToLocalStorage(
        `${t('notificationBuy')} (${receivedAmount} ${tokenReceive.value.label.toUpperCase()})`,
        transactionExchange.hash
      );
      store.updateVersion();

      modifyHistoryHashStatus(transactionExchange?.hash, 'Done');
      store.updateVersion();
      store.updateBuypackCounter();

      balanceKeyA.value++;
      balanceKeyB.value++;

      await pullBalanceA(address.value, tokenATokenAddress.value);
      await pullBalanceB(address.value, tokenBTokenAddress.value);
    } catch (error) {
      console.error('Error:', error);

      if (walletInfo.value?.name) {
        const errorMessage = getCancelErrorMessage(error, walletInfo.value.name);
        $errorHandler(t(errorMessage));
      }
    } finally {
      isExchangeLoading.value = false;
    }
  };

  const closeSuccessModal = (): void => {
    showSuccessModal.value = false;
    exchangeDataPurchased.value = null;
  };

  const debouncedUpdateTokenBAmount = useDebounceFn(async () => {
    await updateTokenBAmount();
  }, 300);

  const debouncedUpdateTokenAAmount = useDebounceFn(async () => {
    await updateTokenAAmount();
  }, 300);

  watch(
    () => tokenA.amount,
    async (newVal, oldVal) => {
      if (newVal !== oldVal && !isProgrammaticUpdate.value) {
        await nextTick();
        await debouncedUpdateTokenBAmount();
      }
    }
  );

  watch(
    () => tokenB.amount,
    async (newVal, oldVal) => {
      if (newVal !== oldVal && !isProgrammaticUpdate.value) {
        await nextTick();
        await debouncedUpdateTokenAAmount();
      }
    }
  );

  watch(
    () => tokenB.key,
    async () => {
      isProgrammaticUpdate.value = true;
      tokenA.amount = TOKEN_A_AMOUNT_MIN.toString();
      tokenB.amount = TOKEN_B_AMOUNT_MIN.toString();
      await nextTick();
      isProgrammaticUpdate.value = false;

      isActualRateLoading.value = true;
      actualRate.value = await getActualRate();
      isActualRateLoading.value = false;

      // Update tokenB amount based on the initial tokenA value
      if (isSellTokenA.value) {
        await updateTokenBAmount();
      } else {
        await updateTokenAAmount();
      }
    }
  );

  watch(isSellTokenA, async (value) => {
    if (value) {
      await updateTokenBAmount();
    } else {
      await updateTokenAAmount();
    }

    await getActualRate();
  });

  const updateTokenAAmount = async () => {
    if (!tokenB.amount || tokenB.amount.trim() === '') {
      isProgrammaticUpdate.value = true;
      tokenA.amount = '';
      await nextTick();
      isProgrammaticUpdate.value = false;
      return;
    }

    if (BigNumber(tokenB.amount).isZero() || BigNumber(tokenB.amount).isLessThan(0)) {
      isProgrammaticUpdate.value = true;
      tokenA.amount = ZERO_VALUE.toString();
      await nextTick();
      isProgrammaticUpdate.value = false;
      return;
    }

    if (isProgrammaticUpdate.value || isNaN(Number(tokenB.amount)) || BigNumber(tokenB.amount).toNumber() < 0) {
      return;
    }

    try {
      isRateTokenALoading.value = true;
      isProgrammaticUpdate.value = true;
      await nextTick();

      const amounts = await getAmountsOut(tokenB.amount, tokenSpendAddress.value, tokenReceiveAddress.value);
      tokenA.amount = BigNumber(formatEther(amounts[1])).toFixed(FRACTIONAL_DIGITS);

      const inputAmount = BigNumber(tokenSpend.value.amount);
      const outputAmount = BigNumber(tokenReceive.value.amount);

      if (inputAmount.gt(0) && outputAmount.gt(0)) {
        const dynamicRate = outputAmount.dividedBy(inputAmount);
        actualRate.value = dynamicRate.toFormat(FRACTIONAL_DIGITS, BigNumber.ROUND_DOWN, {
          groupSeparator: '',
          decimalSeparator: '.'
        });
      } else {
        isActualRateLoading.value = true;
        actualRate.value = await getActualRate();
        isActualRateLoading.value = false;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      await nextTick();
      isProgrammaticUpdate.value = false;
      isRateTokenALoading.value = false;
    }
  };

  const updateTokenBAmount = async () => {
    if (!tokenA.amount || tokenA.amount.trim() === '') {
      isProgrammaticUpdate.value = true;
      tokenB.amount = '';
      await nextTick();
      isProgrammaticUpdate.value = false;
      return;
    }

    if (BigNumber(tokenA.amount).isZero() || BigNumber(tokenA.amount).isLessThan(0)) {
      isProgrammaticUpdate.value = true;
      tokenB.amount = ZERO_VALUE.toString();
      await nextTick();
      isProgrammaticUpdate.value = false;
      return;
    }

    if (isProgrammaticUpdate.value || isNaN(BigNumber(tokenA.amount).toNumber())) {
      return;
    }

    try {
      isRateTokenBLoading.value = true;
      isProgrammaticUpdate.value = true;
      await nextTick();

      const amounts = await getAmountsOut(tokenA.amount, tokenSpendAddress.value, tokenReceiveAddress.value);

      const tokenOutAmountFormatted = formatEther(amounts[1]);
      tokenB.amount = BigNumber(tokenOutAmountFormatted).toFixed(FRACTIONAL_DIGITS);

      const inputAmount = BigNumber(tokenSpend.value.amount);
      const outputAmount = BigNumber(tokenReceive.value.amount);

      if (inputAmount.gt(0) && outputAmount.gt(0)) {
        const dynamicRate = outputAmount.dividedBy(inputAmount);
        actualRate.value = dynamicRate.toFormat(FRACTIONAL_DIGITS, BigNumber.ROUND_DOWN, {
          groupSeparator: '',
          decimalSeparator: '.'
        });
      } else {
        isActualRateLoading.value = true;
        actualRate.value = await getActualRate();
        isActualRateLoading.value = false;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      await nextTick();
      isRateTokenBLoading.value = false;
      isProgrammaticUpdate.value = false;
    }
  };

  const loadDexData = async (): Promise<void> => {
    try {
      isDexDataLoading.value = true;
      dexData.value = await $fetch<DexTokenPairsResponse>(apiUrls.dex, {
        baseURL: apiUrl
      });

      const tokensB = dexData.value?.pairs.map((item: TokenPair) => {
        const { address, name, projectToken } = item.tokens[0];

        return {
          address,
          name,
          key: toCamelCase(name),
          isSkipApprove: projectToken
        };
      });

      availableTokensB.value = [...new Set(tokensB)] as DexTokenTransformed[];

      const tokensA = dexData.value?.pairs.map((item: TokenPair) => {
        const { address, name, projectToken } = item.tokens[1];

        return {
          address,
          name,
          key: toCamelCase(name),
          isSkipApprove: projectToken
        };
      });

      availableTokensA.value = [...new Set(tokensA)] as DexTokenTransformed[];

      const pearlTokenA = availableTokensA.value.find((token) => token.key === Tokens.pearl);
      const pearlTokenB = availableTokensB.value.find((token) => token.key === Tokens.pearl);

      const { key: keyA, name: nameA } = pearlTokenA || availableTokensA.value[0];
      const { key: keyB, name: nameB } = pearlTokenB || availableTokensB.value[0];

      tokenA.key = keyA;
      tokenA.label = (tokensConfig as Record<string, { methodName?: string }>)[keyA]?.methodName ?? nameA;
      tokenA.icon = tokens[keyA as Tokens] as string;

      tokenB.key = keyB;
      tokenB.label = (tokensConfig as Record<string, { methodName?: string }>)[keyB]?.methodName ?? nameB;
      tokenB.icon = tokens[keyB as Tokens] as string;
    } catch (error) {
      console.error(error);
      dexData.value = null;
    } finally {
      isDexDataLoading.value = false;
    }
  };

  const tokenOptionsForSegmented = computed(() => {
    const options = dexData.value?.pairs.map((item: TokenPair) => {
      const tokenName = toCamelCase(item.tokens[0].name);

      if (isToken(tokenName)) {
        return {
          value: tokenName,
          icon: tokens[tokenName as Tokens]
        };
      }

      const token = findTokenByPartialKey(item.tokens[0].name);
      return {
        value: token,
        icon: tokens[token as Tokens]
      };
    });

    return options ? sortByTokenPriority(options, (item) => item.value as string) : options;
  });

  watch(address, async (newAddress) => {
    if (newAddress) {
      balanceKeyA.value++;
      balanceKeyB.value++;

      await pullBalanceA(newAddress, usdtContract.value);
      await pullBalanceB(newAddress, tokenBTokenAddress.value);
    }
  });

  const { pause: pauseRateUpdate, resume: resumeRateUpdate } = useIntervalFn(async () => {
    if (address.value && !isExchangeLoading.value && isModalVisible?.value) {
      const previousRate = actualRate.value;

      const newRate = await getActualRate();

      if (previousRate !== newRate) {
        isActualRateLoading.value = true;
        actualRate.value = newRate;
        isActualRateLoading.value = false;

        if (isSellTokenA.value) {
          isRateTokenBLoading.value = true;
          await updateTokenBAmount();
        } else {
          isRateTokenALoading.value = true;
          await updateTokenAAmount();
        }
      }
    }
  }, RATE_UPDATE_INTERVAL);

  const setMaxBalanceA = () => {
    if (isSellTokenA.value) {
      tokenA.amount = BigNumber(balanceA.value ?? 0).toString();
    }
  };

  const setMaxBalanceB = () => {
    if (!isSellTokenA.value) {
      tokenB.amount = BigNumber(balanceB.value ?? 0).toString();
    }
  };

  watch(
    isModalVisible,
    (isVisible) => {
      if (isVisible) {
        resumeRateUpdate();
      } else {
        console.log('pauseRateUpdate');
        pauseRateUpdate();
      }
    },
    { immediate: true }
  );

  return {
    tokenA,
    tokenB,
    tokenA_amount,
    tokenB_amount,
    tokenA_balance,
    tokenB_balance,
    tokenATokenAddress,
    tokenBTokenAddress,
    balanceTokenAKey,
    balanceTokenBKey,
    tokenAOptions: availableTokenAOptionsExcludingSelected,
    tokenBOptions: availableTokenBOptionsExcludingSelected,
    swapTokens,
    exchange,
    changeToken,
    loadDexData,
    getActualRate,
    closeSuccessModal,
    showSuccessModal,
    showConfirmModal,
    exchangeData,
    exchangeDataPurchased,
    isAnimating,
    isSwapped,
    isSellTokenA,
    isDexDataLoading,
    isRateTokenALoading,
    isRateTokenBLoading,
    isEnoughBalance,
    isActualRateLoading,
    isExchangeLoading,
    dexData,
    actualRate,
    actualRateFormatted,
    tokenOptionsForSegmented,
    availableTokenBOptions,
    setMaxBalanceA,
    setMaxBalanceB,
    tokenSpend
  };
};
